/**
 * StagingGate
 *
 * Wraps the entire Dialer app with a password prompt when VITE_STAGING_PASSWORD
 * is defined at build time. If the env var is absent (production build), this
 * component renders children immediately with no gate.
 *
 * Security note: VITE_* env vars are embedded in the JavaScript bundle at build
 * time — they are NOT secret. A determined attacker can find the password by
 * inspecting the bundle. This is an intentional trade-off for a static Vite app:
 * the gate is designed to prevent casual/accidental access to staging, not to
 * protect sensitive data (staging contains no production data).
 *
 * The validated password is stored in sessionStorage so the user is not prompted
 * again after a page refresh within the same browser session.
 */

import { useState, useEffect, type FormEvent, type ReactNode } from "react";

const SESSION_KEY = "staging_auth";

interface StagingGateProps {
  children: ReactNode;
}

export function StagingGate({ children }: StagingGateProps) {
  // Cast to string | undefined because the var may not be defined at build time.
  const stagingPassword = import.meta.env.VITE_STAGING_PASSWORD as string | undefined;

  // If no password is configured, skip the gate entirely.
  const [authenticated, setAuthenticated] = useState(!stagingPassword);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  // On mount, check whether the session already has a valid token.
  useEffect(() => {
    if (!stagingPassword) return;
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored === stagingPassword) {
      setAuthenticated(true);
    }
  }, [stagingPassword]);

  if (authenticated) return <>{children}</>;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (password === stagingPassword) {
      sessionStorage.setItem(SESSION_KEY, password);
      setAuthenticated(true);
    } else {
      setError("Incorrect password. Try again.");
    }
  };

  return (
    <main
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: "#f9fafb",
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          width: "100%",
          maxWidth: "360px",
          padding: "2rem",
          background: "white",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <div>
          <h1 style={{ margin: "0 0 0.25rem", fontSize: "1.25rem" }}>Staging environment</h1>
          <p style={{ margin: 0, color: "#6b7280", fontSize: "0.875rem" }}>
            This is a protected preview environment. Enter the password to continue.
          </p>
        </div>

        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Staging password"
          autoFocus
          style={{
            padding: "0.5rem 0.75rem",
            fontSize: "1rem",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            outline: "none",
          }}
        />

        {error && <p style={{ margin: 0, color: "#dc2626", fontSize: "0.875rem" }}>{error}</p>}

        <button
          type="submit"
          disabled={!password}
          style={{
            padding: "0.5rem",
            fontSize: "1rem",
            background: !password ? "#93c5fd" : "#2563eb",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: !password ? "not-allowed" : "pointer",
          }}
        >
          Enter
        </button>
      </form>
    </main>
  );
}
